import React, { useRef, useEffect, useState } from 'react';
import { pie, arc } from 'd3';
import type { PieArcDatum } from 'd3';

const DEFAULT_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B6B', '#4ECDC4', '#45B7D1'];

interface D3PieChartProps {
  data: Array<Record<string, any>>;
  dataKey: string;
  nameKey: string;
  colors?: string[];
  height?: number;
  outerRadius?: number;
  showTooltip?: boolean;
  showLegend?: boolean;
  showLabel?: boolean;
}

interface TooltipState {
  x: number;
  y: number;
  name: string;
  value: number;
}

export const D3PieChart: React.FC<D3PieChartProps> = ({
  data,
  dataKey,
  nameKey,
  colors = DEFAULT_COLORS,
  height = 300,
  outerRadius = 100,
  showTooltip = true,
  showLegend = false,
  showLabel = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(400);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setWidth(el.clientWidth || 400);
    const ro = new ResizeObserver(entries => {
      setWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!data.length) return null;

  const cx = width / 2;
  const cy = height / 2;
  const r = Math.min(outerRadius, Math.min(cx, cy) - 10);

  const pieGen = pie<Record<string, any>>().value(d => Number(d[dataKey])).sort(null);
  const arcGen = arc<PieArcDatum<Record<string, any>>>().outerRadius(r).innerRadius(0);
  const labelArcGen = arc<PieArcDatum<Record<string, any>>>().outerRadius(r * 0.75).innerRadius(r * 0.4);

  const slices = pieGen(data);
  const total = slices.reduce((s, d) => s + d.value, 0);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <svg width={width} height={height} onMouseLeave={() => setTooltip(null)}>
        <g transform={`translate(${cx},${cy})`}>
          {slices.map((d, i) => {
            const fill = colors[i % colors.length];
            const pathD = arcGen(d) ?? '';
            const pct = total > 0 ? ((d.value / total) * 100).toFixed(0) : '0';
            const [lx, ly] = labelArcGen.centroid(d);
            const bigEnough = d.endAngle - d.startAngle > 0.4;
            return (
              <g key={i}>
                <path
                  d={pathD}
                  fill={fill}
                  stroke="#fff"
                  strokeWidth={1.5}
                  onMouseMove={showTooltip ? e => {
                    const rect = containerRef.current!.getBoundingClientRect();
                    setTooltip({
                      x: e.clientX - rect.left,
                      y: e.clientY - rect.top,
                      name: String(d.data[nameKey]),
                      value: d.value,
                    });
                  } : undefined}
                  style={{ cursor: 'default' }}
                />
                {showLabel && bigEnough && (
                  <text
                    transform={`translate(${lx},${ly})`}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={10}
                    fill="#fff"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  >
                    {`${d.data[nameKey]}: ${pct}%`}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {showTooltip && tooltip && (
        <div style={{
          position: 'absolute',
          left: tooltip.x + 12,
          top: tooltip.y - 36,
          background: 'var(--mg-bg-elevated, #fff)',
          border: '1px solid var(--mg-border, #e8e8e8)',
          borderRadius: 6,
          padding: '5px 10px',
          fontSize: 12,
          pointerEvents: 'none',
          zIndex: 10,
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        }}>
          <strong>{tooltip.name}</strong>: {tooltip.value.toLocaleString()}
        </div>
      )}

      {showLegend && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px 16px',
          justifyContent: 'center',
          marginTop: 4,
          paddingBottom: 8,
        }}>
          {data.map((d, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <span style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: colors[i % colors.length],
                flexShrink: 0,
                display: 'inline-block',
              }} />
              <span style={{ color: 'var(--mg-text-secondary, #666)' }}>{String(d[nameKey])}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
